import { useEffect, useState } from 'react';
import { Plus, GripVertical, Eye, Save, ChevronDown, ChevronUp, Trash2, LayoutTemplate, Archive, ArchiveRestore, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import DynamicForm from '../../components/DynamicForm';

const FIELD_TYPES = ['text', 'email', 'phone', 'number', 'textarea', 'select', 'radio', 'checkbox', 'date', 'file', 'switch', 'header', 'user_picker'];

/**
 * Form builder. Pick a schema (or create one) → add steps → add fields →
 * preview live → save. Sales side automatically picks up the active schema.
 */
export default function FormBuilder() {
    const [list, setList] = useState([]);
    const [picked, setPicked] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadList = async () => {
        const { data } = await api.get('/forms/schemas');
        setList(data.items);
        if (data.items[0] && !picked) await loadOne(data.items[0]._id);
        if (data.items.length === 0) {
            // bootstrap default
            const seed = await api.get('/forms/schemas/active');
            setPicked(seed.data);
            const { data: again } = await api.get('/forms/schemas');
            setList(again.items);
        }
    };
    const loadOne = async (id) => {
        const { data } = await api.get(`/forms/schemas/${id}`);
        setPicked(data);
    };
    useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

    const save = async () => {
        if (!picked?._id) return;
        setBusy(true);
        try {
            await api.patch(`/forms/schemas/${picked._id}`, {
                name: picked.name, description: picked.description, steps: picked.steps, isActive: picked.isActive,
            });
            toast.success('Schema saved');
            loadList();
        } finally { setBusy(false); }
    };

    const newSchema = async () => {
        const name = prompt('Form name (e.g. "VIP Onboarding"):');
        if (!name) return;
        const slug = (prompt('Slug:', name.toLowerCase().replace(/\s+/g, '-')) || '').trim();
        if (!slug) return;
        const { data } = await api.post('/forms/schemas', { name, slug, steps: [{ title: 'Step 1', fields: [] }] });
        toast.success('Created');
        await loadList();
        loadOne(data._id);
    };

    const addStep = () => setPicked(p => ({ ...p, steps: [...p.steps, { title: `Step ${p.steps.length + 1}`, fields: [] }] }));
    const setStep = (idx, patch) => setPicked(p => ({ ...p, steps: p.steps.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
    const moveStep = (idx, dir) => setPicked(p => {
        const next = [...p.steps];
        const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return p;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        return { ...p, steps: next };
    });
    const removeStep = (idx) => setPicked(p => ({ ...p, steps: p.steps.filter((_, i) => i !== idx) }));

    const addField = (stepIdx) => setStep(stepIdx, {
        fields: [...picked.steps[stepIdx].fields, { key: `field_${Date.now().toString(36)}`, label: 'New field', type: 'text', width: 'full' }],
    });
    const setField = (stepIdx, fieldIdx, patch) => setStep(stepIdx, {
        fields: picked.steps[stepIdx].fields.map((f, i) => {
            if (i !== fieldIdx) return f;
            const next = { ...f, ...patch };
            if (Object.prototype.hasOwnProperty.call(patch, 'visibleIf') && patch.visibleIf == null) delete next.visibleIf;
            return next;
        }),
    });
    const moveField = (stepIdx, fieldIdx, dir) => setStep(stepIdx, {
        fields: (() => {
            const a = [...picked.steps[stepIdx].fields];
            const t = fieldIdx + dir;
            if (t < 0 || t >= a.length) return a;
            [a[fieldIdx], a[t]] = [a[t], a[fieldIdx]];
            return a;
        })(),
    });
    const removeField = (stepIdx, fieldIdx) => setStep(stepIdx, {
        fields: picked.steps[stepIdx].fields.filter((_, i) => i !== fieldIdx),
    });
    /* Soft archive — NEVER hard-delete. Data attached to this key stays
       intact and the field is still rendered in the Client Ledger detail
       drawer (with an "archived" badge) but disappears from the sales form. */
    const archiveField = async (stepIdx, fieldIdx) => {
        const field = picked.steps[stepIdx].fields[fieldIdx];
        if (!confirm(`Archive "${field.label}"? Existing submission data is preserved; the field just stops showing on the sales form.`)) return;
        try {
            const { data } = await api.post(`/forms/schemas/${picked._id}/archive-field`, { stepIdx, fieldKey: field.key });
            setPicked(data);
            toast.success(`Archived "${field.label}"`);
        } catch {/* toasted */ }
    };
    const restoreField = async (stepIdx, fieldIdx) => {
        const field = picked.steps[stepIdx].fields[fieldIdx];
        try {
            const { data } = await api.post(`/forms/schemas/${picked._id}/restore-field`, { stepIdx, fieldKey: field.key });
            setPicked(data);
            toast.success(`Restored "${field.label}"`);
        } catch {/* toasted */ }
    };

    /** Pull every step/field from `backend/config/defaultClientRegistrationSteps.js` into this schema. */
    const loadClientRegistrationTemplate = async () => {
        if (!picked?._id) return;
        if (!confirm(
            'Replace ALL steps and fields on this form with the full client-registration template?\n\n'
            + 'This overwrites the current layout and saves immediately to the database. '
            + 'Existing submissions are unchanged; archived keys are not merged here.'
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/forms/schemas/${picked._id}/apply-client-registration-template`);
            setPicked(data);
            toast.success('Client registration template applied and saved.');
            loadList();
        } catch {
            toast.error('Could not load template');
        } finally {
            setBusy(false);
        }
    };

    if (!picked) return null;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-extrabold">Form builder</h1>
                    <p className="text-sm text-ink-500">
                        Define the multi-step form once. Sales <code>/sales/clients/new</code> renders it automatically.
                        Use <strong className="font-semibold text-ink-700">Load client registration template</strong> to import every field from <code className="font-mono text-xs">defaultClientRegistrationSteps.js</code> into the selected schema.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <select className="ag-input w-56" value={picked._id} onChange={e => loadOne(e.target.value)}>
                        {list.map(s => <option key={s._id} value={s._id}>{s.name} · v{s.version}</option>)}
                    </select>
                    <button className="ag-btn-ghost" onClick={newSchema}><Plus className="h-4 w-4" /> New schema</button>
                    <button type="button" className="ag-btn-outline" onClick={loadClientRegistrationTemplate} disabled={busy} title="Loads every step/field from defaultClientRegistrationSteps.js">
                        <LayoutTemplate className="h-4 w-4" /> Load client registration template
                    </button>
                    <button className="ag-btn-ghost" onClick={() => setShowPreview(p => !p)}><Eye className="h-4 w-4" /> {showPreview ? 'Hide' : 'Preview'}</button>
                    <button className="ag-btn-primary" onClick={save} disabled={busy}><Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save'}</button>
                </div>
            </div>

            <div className="ag-card p-5 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div>
                    <label className="ag-label">Form name</label>
                    <input className="ag-input" value={picked.name} onChange={e => setPicked(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="lg:col-span-2">
                    <label className="ag-label">Description</label>
                    <input className="ag-input" value={picked.description || ''} onChange={e => setPicked(p => ({ ...p, description: e.target.value }))} />
                </div>
            </div>

            {/* Steps */}
            <div className="space-y-3">
                {picked.steps.map((step, sIdx) => (
                    <div key={sIdx} className="ag-card p-4">
                        <div className="flex flex-wrap items-end gap-3 mb-3">
                            <div className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold">Step {sIdx + 1}</div>
                            <input className="ag-input flex-1 min-w-[200px]" value={step.title} onChange={e => setStep(sIdx, { title: e.target.value })} placeholder="Step title" />
                            <input className="ag-input flex-1 min-w-[200px]" value={step.description || ''} onChange={e => setStep(sIdx, { description: e.target.value })} placeholder="Optional description" />
                            <div className="flex gap-1">
                                <button className="ag-btn-ghost text-xs" onClick={() => moveStep(sIdx, -1)}><ChevronUp className="h-3.5 w-3.5" /></button>
                                <button className="ag-btn-ghost text-xs" onClick={() => moveStep(sIdx, 1)}><ChevronDown className="h-3.5 w-3.5" /></button>
                                <button className="ag-btn-danger text-xs" onClick={() => removeStep(sIdx)}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {step.fields.map((f, fIdx) => (
                                <FieldRow key={fIdx} field={f}
                                    onChange={(p) => setField(sIdx, fIdx, p)}
                                    onUp={() => moveField(sIdx, fIdx, -1)}
                                    onDown={() => moveField(sIdx, fIdx, 1)}
                                    onArchive={() => archiveField(sIdx, fIdx)}
                                    onRestore={() => restoreField(sIdx, fIdx)}
                                />
                            ))}
                            <button className="ag-btn-ghost w-full justify-center" onClick={() => addField(sIdx)}>
                                <Plus className="h-4 w-4" /> Add field
                            </button>
                        </div>
                    </div>
                ))}
                <button className="ag-btn-outline w-full justify-center" onClick={addStep}>
                    <Plus className="h-4 w-4" /> Add step
                </button>
            </div>

            {showPreview && (
                <div className="ag-card p-5">
                    <h3 className="font-bold mb-2">Live preview</h3>
                    <DynamicForm schema={picked} onSubmit={() => toast('Preview only — submit is disabled here.')} />
                </div>
            )}
        </div>
    );
}

/** visibleIf.equals may be a string or string[] (stored in Mongo as Mixed). */
function formatVisibleIfEquals(eq) {
    if (eq == null || eq === '') return '';
    if (Array.isArray(eq)) return eq.join(', ');
    return String(eq);
}

/** Comma-separated → array of strings; single token → scalar string. */
function parseVisibleIfEquals(input) {
    const raw = String(input ?? '').trim();
    if (!raw) return '';
    const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return parts;
}

function FieldRow({ field, onChange, onUp, onDown, onArchive, onRestore }) {
    const [open, setOpen] = useState(false);
    const archived = !!field.isArchived;
    return (
        <div className={`border rounded-lg p-3 transition relative ${archived
                ? 'border-amber-200 bg-amber-50/60 opacity-90'
                : 'border-ink-100 bg-ink-50/50'
            }`}>
            {archived && (
                <div className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold ring-1 ring-amber-200">
                    <EyeOff className="h-3 w-3" /> Archived · hidden from sales form
                </div>
            )}
            <div className={`flex flex-wrap items-end gap-2 ${archived ? 'pr-44' : ''}`}>
                <GripVertical className="h-4 w-4 text-ink-300 mt-2" />
                <div className="flex-1 min-w-[140px]">
                    <label className="ag-label">Label</label>
                    <input className="ag-input" value={field.label} onChange={e => onChange({ label: e.target.value })} />
                </div>
                <div className="min-w-[140px]">
                    <label className="ag-label">Key</label>
                    <input className="ag-input font-mono text-xs" value={field.key} onChange={e => onChange({ key: e.target.value.replace(/[^A-Za-z0-9_]/g, '_') })} />
                </div>
                <div>
                    <label className="ag-label">Type</label>
                    <select className="ag-input" value={field.type} onChange={e => onChange({ type: e.target.value })}>
                        {FIELD_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className="ag-label">Width</label>
                    <select className="ag-input" value={field.width || 'full'} onChange={e => onChange({ width: e.target.value })}>
                        <option value="full">full</option><option value="half">half</option><option value="third">third</option>
                    </select>
                </div>
                <label className="flex items-center gap-1 text-xs ag-btn-ghost">
                    <input type="checkbox" checked={!!field.required} onChange={e => onChange({ required: e.target.checked })} /> Required
                </label>
                <div className="flex gap-1">
                    <button type="button" className="ag-btn-ghost text-xs" onClick={() => setOpen(o => !o)} title="Placeholder, help, file accept, conditional visibility">{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>
                    <button type="button" className="ag-btn-ghost text-xs" onClick={onUp}><ChevronUp className="h-3.5 w-3.5 rotate-90" /></button>
                    <button type="button" className="ag-btn-ghost text-xs" onClick={onDown}><ChevronDown className="h-3.5 w-3.5 rotate-90" /></button>
                    {archived ? (
                        <button type="button" className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1.5 text-[11px] font-bold" onClick={onRestore} title="Restore field — show on form again">
                            <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                        </button>
                    ) : (
                        <button type="button" className="inline-flex items-center gap-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 ring-1 ring-amber-200 px-2 py-1.5 text-[11px] font-bold" onClick={onArchive} title="Archive — keep data, hide from sales form (NEVER deletes)">
                            <Archive className="h-3.5 w-3.5" /> Archive
                        </button>
                    )}
                </div>
            </div>

            {(field.type === 'select' || field.type === 'radio') && (
                <div className="mt-3 ml-6 sm:ml-8 pl-3 border-l-2 border-brand-teal/50">
                    <label className="ag-label">Options {field.type === 'select' ? '(dropdown)' : '(radio buttons)'}</label>
                    <p className="text-[11px] text-ink-500 mb-1.5">
                        One per line. Format <code className="font-mono text-[10px]">storedValue | Shown label</code> — or a single word to use the same text for both (e.g. <code className="font-mono text-[10px]">Noida</code>).
                    </p>
                    <textarea className="ag-input min-h-[88px] font-mono text-xs w-full max-w-2xl"
                        value={(field.options || []).map(o => o.value === o.label ? o.label : `${o.value} | ${o.label}`).join('\n')}
                        onChange={e => onChange({
                            options: e.target.value.split(/\n+/).filter(Boolean).map(line => {
                                const [a, b] = line.split('|').map(s => s.trim());
                                return b ? { value: a, label: b } : { value: a, label: a };
                            })
                        })} />
                </div>
            )}

            {open && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    <div>
                        <label className="ag-label">Placeholder</label>
                        <input className="ag-input" value={field.placeholder || ''} onChange={e => onChange({ placeholder: e.target.value })} />
                    </div>
                    <div className="lg:col-span-2">
                        <label className="ag-label">Help text</label>
                        <input className="ag-input" value={field.helpText || ''} onChange={e => onChange({ helpText: e.target.value })} />
                    </div>
                    {field.type === 'file' && (
                        <div>
                            <label className="ag-label">Accept</label>
                            <input className="ag-input font-mono text-xs" value={field.accept || ''} onChange={e => onChange({ accept: e.target.value })} placeholder="image/*,.pdf" />
                        </div>
                    )}
                    <div className="sm:col-span-2 lg:col-span-3 border-t border-ink-100 pt-3">
                        <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider mb-2">Conditional visibility</div>
                        <p className="text-[11px] text-ink-500 mb-2">Show this field only when another field matches. Use comma-separated values for “any of” (e.g. <code className="font-mono">Signature, SignatureFico</code>).</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="ag-label">Controller field key</label>
                                <input className="ag-input font-mono text-xs" value={field.visibleIf?.key || ''}
                                    onChange={e => {
                                        const key = e.target.value.trim();
                                        const eqRaw = formatVisibleIfEquals(field.visibleIf?.equals);
                                        if (!key) onChange({ visibleIf: null });
                                        else onChange({ visibleIf: { key, equals: parseVisibleIfEquals(eqRaw) } });
                                    }} placeholder="e.g. franchiseType" />
                            </div>
                            <div>
                                <label className="ag-label">Controller value(s)</label>
                                <input className="ag-input font-mono text-xs" value={formatVisibleIfEquals(field.visibleIf?.equals)}
                                    onChange={e => {
                                        const key = (field.visibleIf?.key || '').trim();
                                        if (!key) return;
                                        onChange({ visibleIf: { key, equals: parseVisibleIfEquals(e.target.value) } });
                                    }} placeholder="One value, or comma-separated list" />
                            </div>
                            <div className="sm:col-span-2">
                                <button type="button" className="ag-btn-ghost text-xs" onClick={() => onChange({ visibleIf: null })}>Clear conditional rule</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
